import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, XCircle, Loader2, ExternalLink, Landmark } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const InvoicePayment = () => {
  const { shareToken } = useParams();

  const [invoice, setInvoice] = useState(null);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/connect/public-invoice/${shareToken}`);
        setInvoice(res.data.invoice);
        setMeta(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invoice not found');
      } finally { setLoading(false); }
    };
    fetch();
  }, [shareToken]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Card className="max-w-md w-full"><CardContent className="py-12 text-center">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Error</h2>
        <p className="text-gray-500 text-sm">{error}</p>
      </CardContent></Card>
    </div>
  );

  const inv = invoice;
  const isPaid = inv?.status === 'paid';
  const bank = meta.bank_details;

  const bankRows = bank ? [
    ['Account Holder', bank.account_name],
    ['Bank Name', bank.bank_name],
    ['Account Number', bank.account_number],
    ['Routing Number', bank.routing_number],
    ['SWIFT / BIC', bank.swift_code],
    ['IBAN', bank.iban],
  ].filter(([, v]) => v) : [];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="invoice-payment-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/logo-mark.png" alt="Miadesk" className="h-10 w-auto mx-auto mb-3" />
          <p className="text-sm text-gray-500">Powered by Miadesk</p>
        </div>

        {isPaid && (
          <Card className="mb-6 border-green-200 bg-green-50" data-testid="payment-success">
            <CardContent className="py-6 text-center">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-green-800">Invoice Paid</h3>
              <p className="text-sm text-green-600 mt-1">Thank you! This invoice has been paid.</p>
            </CardContent>
          </Card>
        )}

        {/* Invoice Card */}
        <Card className="shadow-lg" data-testid="invoice-card">
          <CardContent className="p-6 lg:p-8">
            {/* Invoice Header */}
            <div className="flex items-start justify-between mb-8">
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="invoice-title">Invoice {inv.invoice_number}</h1>
                {meta.business_name && <p className="text-sm text-gray-500 mt-1">From: {meta.business_name}</p>}
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                isPaid ? 'bg-green-100 text-green-700' :
                inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                'bg-amber-100 text-amber-700'
              }`} data-testid="invoice-status">
                {isPaid ? 'Paid' : inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}
              </span>
            </div>

            {/* Bill To */}
            <div className="mb-6 pb-6 border-b border-gray-100">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Bill To</p>
              <p className="text-sm font-medium text-gray-800">{inv.client_name}</p>
              {inv.client_email && <p className="text-xs text-gray-500">{inv.client_email}</p>}
              {inv.due_date && <p className="text-xs text-gray-400 mt-2">Due: {new Date(inv.due_date).toLocaleDateString()}</p>}
            </div>

            {/* Line Items */}
            <table className="w-full mb-6" data-testid="invoice-items">
              <thead>
                <tr className="text-xs text-gray-400 uppercase border-b border-gray-100">
                  <th className="text-left py-2 font-medium">Item</th>
                  <th className="text-right py-2 font-medium">Qty</th>
                  <th className="text-right py-2 font-medium">Rate</th>
                  <th className="text-right py-2 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {inv.items?.map((item, i) => (
                  <tr key={i} className="border-b border-gray-50 text-sm">
                    <td className="py-3 text-gray-800">{item.description}</td>
                    <td className="py-3 text-right text-gray-600">{item.quantity}</td>
                    <td className="py-3 text-right text-gray-600">${Number(item.rate).toFixed(2)}</td>
                    <td className="py-3 text-right font-medium text-gray-800">${Number(item.amount || item.quantity * item.rate).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="space-y-2 mb-8">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-gray-800">${Number(inv.subtotal || inv.total).toFixed(2)}</span>
              </div>
              {inv.tax_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tax</span>
                  <span className="text-gray-800">${Number(inv.tax_amount).toFixed(2)}</span>
                </div>
              )}
              {inv.discount_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Discount</span>
                  <span className="text-green-600">-${Number(inv.discount_amount).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-base pt-2 border-t-2 border-gray-200">
                <span className="text-gray-900 font-bold">Total Due</span>
                <span className="text-gray-900 font-bold text-xl" data-testid="grand-total">${Number(inv.total).toFixed(2)}</span>
              </div>
            </div>

            {/* Notes */}
            {inv.notes && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Notes</p>
                <p className="text-sm text-gray-600">{inv.notes}</p>
              </div>
            )}

            {/* Payment note */}
            {!isPaid && (
              <div className="space-y-4" data-testid="payment-options">
                <p className="text-sm text-gray-500 text-center">Please contact {meta.business_name || 'the sender'} for payment instructions.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvoicePayment;
